// src/services/pdfService.js
import { PDFDocument, degrees, StandardFonts } from 'pdf-lib';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import JSZip from 'jszip';

// Set up PDF.js worker with dynamic version matching
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

export const pdfService = {
  // =====================================
  // FILLABLE PDF FORM FUNCTIONS
  // =====================================

  /**
   * Load PDF file and convert each page to a high-resolution image
   * Used for the fillable form editor to display pages as backgrounds
   * @param {File} file - The PDF file to load
   * @returns {Promise<Array>} Array of page data objects with images
   */
  async loadPdfAsImages(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = getDocument({
        data: arrayBuffer,
        useWorkerFetch: true,
        isEvalSupported: false
      });

      const pdf = await loadingTask.promise;
      const pages = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        
        // Scale 2.0 for high resolution rendering (better visual quality)
        const viewport = page.getViewport({ scale: 2.0 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) throw new Error('Could not create canvas context');

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const base64 = canvas.toDataURL('image/png');

        // Store the scale 1.0 viewport dimensions
        // This viewport automatically accounts for the page's internal rotation
        const baseViewport = page.getViewport({ scale: 1.0 });

        pages.push({
          index: i - 1,
          image: base64,
          fields: [],
          isAnalyzed: false,
          width: baseViewport.width,
          height: baseViewport.height
        });

        page.cleanup();
        canvas.remove();
      }

      await pdf.destroy();
      return pages;
    } catch (error) {
      console.error('Error loading PDF as images:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to parse PDF document.');
    }
  },

  /**
   * Export PDF with fillable form fields embedded
   * CRITICAL: Proper coordinate transformation from web (top-left) to PDF (bottom-left)
   * @param {File} originalFile - The original PDF file
   * @param {Array} allPages - Array of page data with field definitions
   * @returns {Promise<Uint8Array>} The fillable PDF bytes
   */
  async exportFillablePdf(originalFile, allPages) {
    try {
      const arrayBuffer = await originalFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const form = pdfDoc.getForm();
      const pdfPages = pdfDoc.getPages();

      // Embed font for form field appearances
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const usedNames = new Set();

      for (const pageData of allPages) {
        const page = pdfPages[pageData.index];
        if (!page) continue;

        // page.getSize() returns dimensions accounting for rotation
        const { width: pageWidth, height: pageHeight } = page.getSize();

        for (const field of pageData.fields) {
          // Generate unique field name
          let safeLabel = field.label.replace(/[^a-zA-Z0-9]/g, '') || 'field';
          let safeId = field.id.replace(/[^a-zA-Z0-9]/g, '');
          let fieldName = `${safeLabel}${safeId}`;

          let counter = 1;
          const baseName = fieldName;
          while (usedNames.has(fieldName)) {
            fieldName = `${baseName}${counter++}`;
          }
          usedNames.add(fieldName);

          /**
           * COORDINATE TRANSFORMATION:
           * Web UI: (0,0) = TOP-LEFT, Y increases downward
           * PDF: (0,0) = BOTTOM-LEFT, Y increases upward
           * 
           * Transformation: y_pdf = pageHeight - y_web - height
           */
          
          const absX = field.x * pageWidth;
          const absY = field.y * pageHeight;
          const absW = field.width * pageWidth;
          const absH = field.height * pageHeight;

          // Convert to PDF coordinates (bottom-left origin)
          const rect = {
            x: absX,
            y: pageHeight - absY - absH,
            width: absW,
            height: absH
          };

          try {
            if (field.type === 'checkbox') {
              const checkbox = form.createCheckBox(fieldName);
              checkbox.addToPage(page, rect);
            } else {
              const textField = form.createTextField(fieldName);
              textField.addToPage(page, rect);
              
              // Dynamic font sizing based on field height
              const fontSize = Math.max(6, Math.min(12, rect.height * 0.7));
              textField.setFontSize(fontSize);

              if (field.type === 'date') {
                textField.setPlaceholderText('MM/DD/YYYY');
              } else if (field.type === 'signature') {
                textField.setPlaceholderText('Signature');
              }
            }
          } catch (fieldError) {
            console.error(`Failed to create field "${fieldName}":`, fieldError);
          }
        }
      }

      try {
        // Update field appearances for visibility in PDF readers
        form.updateFieldAppearances(font);
      } catch (appearanceError) {
        console.warn('Could not update field appearances:', appearanceError);
      }

      return await pdfDoc.save();
    } catch (error) {
      console.error('CRITICAL: Fillable PDF export failed:', error);
      throw error;
    }
  },

  // =====================================
  // COMPRESSION FUNCTIONS
  // =====================================

  // Enhanced PDF compression with multiple strategies
  async compressPDF(pdfFile, compressionLevel = 0.7) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const originalSize = pdfBytes.byteLength;
      
      // Strategy 1: PDF-lib optimization
      let compressedBytes = await this.compressWithPdfLib(pdfBytes, compressionLevel);
      
      // Strategy 2: If minimal compression, try image-based compression
      const pdfLibReduction = (originalSize - compressedBytes.byteLength) / originalSize;
      
      if (pdfLibReduction < 0.1) { // Less than 10% reduction
        console.log('Trying image-based compression...');
        compressedBytes = await this.compressWithImageReduction(pdfFile, compressionLevel);
      }
      
      return new Blob([compressedBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error compressing PDF:', error);
      throw new Error('Failed to compress PDF. The file may be corrupted or unsupported.');
    }
  },

  // PDF-lib based compression with enhanced settings
  async compressWithPdfLib(pdfBytes, compressionLevel) {
    try {
      const pdf = await PDFDocument.load(pdfBytes);
      
      // Remove unused objects and optimize
      const optimizedBytes = await pdf.save({
        useObjectStreams: true,
        addDefaultPage: false,
        objectsPerTick: 50,
        updateFieldAppearances: false,
        // Enhanced compression settings
        compress: true,
        includeXrefTable: false,
        linearize: true
      });
      
      return optimizedBytes;
    } catch (error) {
      console.error('PDF-lib compression failed:', error);
      return pdfBytes; // Return original if compression fails
    }
  },

  // Image-based compression by re-rendering pages
  async compressWithImageReduction(pdfFile, compressionLevel) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const loadingTask = getDocument({
        data: pdfBytes,
        useWorkerFetch: false,
        isEvalSupported: false
      });
      
      const sourcePdf = await loadingTask.promise;
      const newPdf = await PDFDocument.create();
      
      // Determine image quality based on compression level
      const imageQuality = this.getImageQuality(compressionLevel);
      const scale = this.getImageScale(compressionLevel);
      
      for (let pageNum = 1; pageNum <= sourcePdf.numPages; pageNum++) {
        try {
          const page = await sourcePdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });
          
          // Create canvas and render page
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          const context = canvas.getContext('2d');
          await page.render({
            canvasContext: context,
            viewport
          }).promise;
          
          // Convert to JPEG with compression
          const imageDataUrl = canvas.toDataURL('image/jpeg', imageQuality);
          
          // Convert to bytes
          const response = await fetch(imageDataUrl);
          const imageBytes = await response.arrayBuffer();
          
          // Embed image in new PDF
          const image = await newPdf.embedJpg(imageBytes);
          const newPage = newPdf.addPage([viewport.width, viewport.height]);
          
          newPage.drawImage(image, {
            x: 0,
            y: 0,
            width: viewport.width,
            height: viewport.height,
          });
          
          canvas.remove();
        } catch (pageError) {
          console.error(`Error compressing page ${pageNum}:`, pageError);
        }
      }
      
      await sourcePdf.destroy();
      
      const compressedBytes = await newPdf.save({
        useObjectStreams: true,
        compress: true
      });
      
      return compressedBytes;
    } catch (error) {
      console.error('Image-based compression failed:', error);
      // Fallback to basic compression
      return await this.compressWithPdfLib(await pdfFile.arrayBuffer(), compressionLevel);
    }
  },

  // Get image quality based on compression level
  getImageQuality(compressionLevel) {
    if (compressionLevel <= 0.3) return 0.3; // Maximum compression
    if (compressionLevel <= 0.5) return 0.5; // High compression
    if (compressionLevel <= 0.7) return 0.7; // Medium compression
    return 0.85; // Low compression
  },

  // Get image scale based on compression level
  getImageScale(compressionLevel) {
    if (compressionLevel <= 0.3) return 1.0; // Maximum compression - keep resolution
    if (compressionLevel <= 0.5) return 1.2; // High compression
    if (compressionLevel <= 0.7) return 1.5; // Medium compression
    return 2.0; // Low compression - higher resolution
  },

  // Advanced compression with multiple passes
  async advancedCompressPDF(pdfFile, compressionLevel = 0.7) {
    try {
      const originalBytes = await pdfFile.arrayBuffer();
      const originalSize = originalBytes.byteLength;
      
      console.log(`Starting compression of ${(originalSize / 1024 / 1024).toFixed(2)} MB file`);
      
      // Pass 1: Basic PDF optimization
      let currentBytes = await this.compressWithPdfLib(originalBytes, compressionLevel);
      let currentSize = currentBytes.byteLength;
      let reduction = (originalSize - currentSize) / originalSize;
      
      console.log(`Pass 1 - PDF optimization: ${(reduction * 100).toFixed(1)}% reduction`);
      
      // Pass 2: If insufficient compression, try image-based compression
      if (reduction < 0.05 && compressionLevel <= 0.7) {
        const tempFile = new File([currentBytes], 'temp.pdf', { type: 'application/pdf' });
        currentBytes = await this.compressWithImageReduction(tempFile, compressionLevel);
        currentSize = currentBytes.byteLength;
        reduction = (originalSize - currentSize) / originalSize;
        
        console.log(`Pass 2 - Image compression: ${(reduction * 100).toFixed(1)}% total reduction`);
      }
      
      // Pass 3: Final optimization
      if (reduction > 0) {
        try {
          const finalBytes = await this.compressWithPdfLib(currentBytes, compressionLevel);
          const finalSize = finalBytes.byteLength;
          const finalReduction = (originalSize - finalSize) / originalSize;
          
          if (finalReduction > reduction) {
            currentBytes = finalBytes;
            reduction = finalReduction;
            console.log(`Pass 3 - Final optimization: ${(reduction * 100).toFixed(1)}% total reduction`);
          }
        } catch (finalError) {
          console.warn('Final optimization pass failed, using previous result');
        }
      }
      
      return new Blob([currentBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Advanced compression failed:', error);
      throw new Error('Advanced compression failed. Please try a different compression level.');
    }
  },

  // =====================================
  // MERGE & SPLIT FUNCTIONS
  // =====================================

  // Merge multiple PDFs (existing)
  async mergePDFs(pdfFiles) {
    try {
      const mergedPdf = await PDFDocument.create();
      
      for (const file of pdfFiles) {
        const pdfBytes = await file.arrayBuffer();
        const pdf = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach(page => mergedPdf.addPage(page));
      }
      
      const mergedPdfBytes = await mergedPdf.save({
        useObjectStreams: true,
        compress: true
      });
      return new Blob([mergedPdfBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error merging PDFs:', error);
      throw error;
    }
  },

  // Split PDF into separate pages (existing)
  async splitPDF(pdfFile) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdf = await PDFDocument.load(pdfBytes);
      const pageCount = pdf.getPageCount();
      const pages = [];
      
      for (let i = 0; i < pageCount; i++) {
        const newPdf = await PDFDocument.create();
        const [page] = await newPdf.copyPages(pdf, [i]);
        newPdf.addPage(page);
        const pageBytes = await newPdf.save({
          useObjectStreams: true,
          compress: true
        });
        pages.push(new Blob([pageBytes], { type: 'application/pdf' }));
      }
      
      return pages;
    } catch (error) {
      console.error('Error splitting PDF:', error);
      throw error;
    }
  },

  // Split PDF by page ranges
  // ranges can be a string like "1-3, 5, 10-12" or an array of { start, end }
  async splitByRanges(pdfFile, ranges) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdf = await PDFDocument.load(pdfBytes);
      const totalPages = pdf.getPageCount();
      const results = [];

      // Parse string ranges into { start, end } objects
      let parsedRanges = ranges;
      if (typeof ranges === 'string') {
        parsedRanges = ranges
          .split(',')
          .map(part => part.trim())
          .filter(part => part.length > 0)
          .map(part => {
            if (part.includes('-')) {
              const [s, e] = part.split('-').map(Number);
              return { start: s, end: e };
            }
            const page = Number(part);
            return { start: page, end: page };
          })
          .filter(r => !isNaN(r.start) && !isNaN(r.end));
      }

      for (const range of parsedRanges) {
        const { start, end } = range;
        if (start < 1 || end > totalPages || start > end) continue;

        const newPdf = await PDFDocument.create();
        const indices = Array.from(
          { length: end - start + 1 },
          (_, i) => start - 1 + i
        );

        const copiedPages = await newPdf.copyPages(pdf, indices);
        copiedPages.forEach(page => newPdf.addPage(page));

        const bytes = await newPdf.save({ useObjectStreams: true, compress: true });
        results.push({
          blob: new Blob([bytes], { type: 'application/pdf' }),
          rangeLabel: start === end ? `Page ${start}` : `Pages ${start}-${end}`
        });
      }

      if (results.length === 0) throw new Error("No valid ranges provided");
      return results;
    } catch (error) {
      console.error('Error splitting by range:', error);
      throw error;
    }
  },

  // =====================================
  // SECURITY FUNCTIONS
  // =====================================

  async protectPDF(pdfFile, password, permissions = {}) {
    try {
      // 1. Convert file to array buffer
      const pdfBytes = await pdfFile.arrayBuffer();
      
      // 2. Load the document
      const pdfDoc = await PDFDocument.load(pdfBytes);
      
      // 3. Define the owner password to lock down permissions
      const ownerPassword = `owner_${Math.random().toString(36).slice(2)}_${password}`;

      // 4. Critical: The 'save' method must contain the encryption keys
      const protectedBytes = await pdfDoc.save({
        userPassword: password, // Password required to open the PDF
        ownerPassword: ownerPassword, // Password required to change permissions
        permissions: {
          printing: permissions.allowPrinting ? 'highResolution' : 'none',
          modifying: permissions.allowModifying ?? false,
          copying: permissions.allowCopying ?? false,
          annotating: permissions.allowAnnotating ?? false,
          fillingForms: permissions.allowFillingForms ?? false,
          contentAccessibility: true, // Recommended for screen readers
          documentAssembly: permissions.allowModifying ?? false,
        },
      });

      // 5. Return as a fresh Blob to ensure the browser doesn't use a cached version
      return new Blob([protectedBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Security Error:', error);
      throw new Error('Failed to encrypt. The PDF might already be protected or restricted.');
    }
  },

  // =====================================
  // CONVERSION FUNCTIONS
  // =====================================

  // Convert PDF pages to JPEG (existing - with optimization)
  async convertToJPEG(pdfFile, quality = 2) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      
      const loadingTask = getDocument({
        data: pdfBytes,
        useWorkerFetch: false,
        isEvalSupported: false,
        useSystemFonts: true,
        disableAutoFetch: true,
        disableStream: true
      });
      
      const pdf = await loadingTask.promise;
      const zip = new JSZip();
      
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        try {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: quality });
          
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          const context = canvas.getContext('2d');
          
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
            background: 'white',
            enableWebGL: false
          };
          
          await page.render(renderContext).promise;
          
          const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
          const base64Data = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
          zip.file(`page-${pageNumber}.jpeg`, base64Data, { base64: true });
          
          canvas.remove();
        } catch (pageError) {
          console.error(`Error processing page ${pageNumber}:`, pageError);
        }
      }
      
      await pdf.destroy();
      return await zip.generateAsync({ type: 'blob' });
    } catch (error) {
      console.error('Error converting PDF to JPEG:', error);
      throw new Error('Failed to convert PDF to JPEG. Please ensure the PDF is valid and try again.');
    }
  },

  // =====================================
  // PAGE MANIPULATION FUNCTIONS
  // =====================================

  // Rotate PDF pages (existing - with optimization)
  async rotatePDF(pdfFile, rotations) {
    try {
      const pdfBytes = await (pdfFile instanceof Blob ? pdfFile.arrayBuffer() : pdfFile.arrayBuffer());
      const pdf = await PDFDocument.load(pdfBytes);
      const pages = pdf.getPages();
      
      rotations.forEach((rotation, index) => {
        if (pages[index] && rotation !== 0) {
          pages[index].setRotation(degrees(rotation));
        }
      });
      
      const rotatedBytes = await pdf.save({
        useObjectStreams: true,
        compress: true
      });
      return new Blob([rotatedBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error rotating PDF:', error);
      throw error;
    }
  },

  // Reorder PDF pages (existing - with optimization)
  async reorderPages(pdfFile, newOrder) {
    try {
      const pdfBytes = await (pdfFile instanceof Blob ? pdfFile.arrayBuffer() : pdfFile.arrayBuffer());
      const sourcePdf = await PDFDocument.load(pdfBytes);
      const newPdf = await PDFDocument.create();
      
      for (const pageIndex of newOrder) {
        const [page] = await newPdf.copyPages(sourcePdf, [pageIndex]);
        newPdf.addPage(page);
      }
      
      const reorderedBytes = await newPdf.save({
        useObjectStreams: true,
        compress: true
      });
      return new Blob([reorderedBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error reordering PDF pages:', error);
      throw error;
    }
  },

  // =====================================
  // WATERMARK & TEXT FUNCTIONS
  // =====================================

  // Add watermark to PDF (existing)
  async addWatermark(pdfFile, watermarkText, options = {}) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdf = await PDFDocument.load(pdfBytes);
      const pages = pdf.getPages();
      
      const {
        fontSize = 50,
        opacity = 0.3,
        rotation = 45,
        color = [0.7, 0.7, 0.7]
      } = options;
      
      pages.forEach(page => {
        const { width, height } = page.getSize();
        page.drawText(watermarkText, {
          x: width / 2 - (watermarkText.length * fontSize) / 4,
          y: height / 2,
          size: fontSize,
          color: { r: color[0], g: color[1], b: color[2] },
          opacity,
          rotate: degrees(rotation)
        });
      });
      
      const watermarkedBytes = await pdf.save({
        useObjectStreams: true,
        compress: true
      });
      return new Blob([watermarkedBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error adding watermark:', error);
      throw error;
    }
  },

  // Extract text from PDF (existing)
  async extractText(pdfFile) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const loadingTask = getDocument({
        data: pdfBytes,
        useWorkerFetch: false,
        isEvalSupported: false
      });
      
      const pdf = await loadingTask.promise;
      let fullText = '';
      
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        try {
          const page = await pdf.getPage(pageNumber);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          fullText += `Page ${pageNumber}:\n${pageText}\n\n`;
        } catch (pageError) {
          console.error(`Error extracting text from page ${pageNumber}:`, pageError);
        }
      }
      
      await pdf.destroy();
      return fullText;
    } catch (error) {
      console.error('Error extracting text:', error);
      throw error;
    }
  }
};